// Чистата логика на таблото „Склад": какво казва всяка кутия (обобщенията от
// данните на операцията), колко силен е сигналът ѝ (критично / внимание) и как
// клавиатурата се движи между кутиите. Рендерът е в `components/`, заявките —
// в `useDashboardSources`.
//
// Кутиите са ОПЕРАЦИИТЕ от менюто в неговия ред (`OPERATIONS_NAV_GROUP`) —
// клавиш 1–8 отваря N-тата; нова операция в менюто = нова кутия (и тестът
// пази, че всяка има обобщение).
import dayjs, { type Dayjs } from "dayjs";
import type { InterchangeGroup, PendingReport, Warehouse, Writeoff } from "../../../api/inventory";
import type { PayableDelivery } from "../../../api/inventoryDeliveries";
import type { LatestStocktake } from "../../../api/inventoryStocktakes";
import type { TransferDocument } from "../../../api/inventoryTransfers";
import { OPERATIONS_NAV_GROUP } from "../components/shell/inventoryNav";
import { roundMoney } from "../components/numbers";
import type { DraftSummary } from "../components/drafts/draftModel";
import { OVERDUE_PAYMENT_DAYS } from "../delivery/components/deliveryMeta";
import { payableAgeDays } from "../payments/deliveryPaymentsModel";
import { isReportOverdue } from "../posting/components/pendingReportAge";
import type { ShortageRow } from "../shortage/components/shortageShared";
import { enteredAtOf, isTransferAnnulled } from "../transfer/components/transferMeta";
import { sumActiveWriteoffs } from "../writeoff/components/writeoffMeta";

// ─── Състоянието на една кутия ─────────────────────────────────────────────
// off — локален режим (касата не знае за склада); error — източникът падна
// (кутията пак се отваря, другите кутии не са засегнати).
export type Loadable<T> =
  | { state: "loading" }
  | { state: "error" }
  | { state: "off" }
  | { state: "ready"; value: T };

type QueryLike = { data?: unknown; isError: boolean };

// Готово, когато ВСИЧКИ източници на кутията имат данни; грешка, когато някой
// падне без данни. Вече заредени данни остават и при неуспешно опресняване.
export const loadStateOf = (
  queries: QueryLike[],
  isOff: boolean,
): Loadable<unknown>["state"] => {
  if (isOff) return "off";
  if (queries.every((query) => query.data !== undefined)) return "ready";
  if (queries.some((query) => query.isError && query.data === undefined)) return "error";
  return "loading";
};

export const loadableOf = <T>(
  queries: QueryLike[],
  isOff: boolean,
  summarize: () => T,
): Loadable<T> => {
  const state = loadStateOf(queries, isOff);
  return state === "ready" ? { state, value: summarize() } : { state };
};

// Календарни дни между датата и днес (днешната = 0; бъдеща дата — също 0)
export const calendarDaysSince = (date: string, now: Dayjs = dayjs()) =>
  Math.max(0, now.startOf("day").diff(dayjs(date).startOf("day"), "day"));

// ─── 1 Разнасяне ───────────────────────────────────────────────────────────
export type PostingSummary = {
  pending: number;
  overdue: number;
  // Най-старият чакащ — „Разнеси най-стария" го отваря избран
  oldest: { id: number; reportNumber: number; reportDate: string } | null;
};

export const summarizePosting = (reports: PendingReport[], now: Dayjs = dayjs()): PostingSummary => {
  const oldest = [...reports].sort(
    (a, b) => a.reportDate.localeCompare(b.reportDate) || a.reportNumber - b.reportNumber,
  )[0];
  return {
    pending: reports.length,
    overdue: reports.filter((report) => isReportOverdue(report.reportDate, now)).length,
    oldest: oldest
      ? { id: oldest.id, reportNumber: oldest.reportNumber, reportDate: oldest.reportDate }
      : null,
  };
};

// ─── 2 Доставка ────────────────────────────────────────────────────────────
export type DeliveryDraftsSummary = {
  // незавършените на ресторанта: моята + на колегите
  total: number;
  mine: DraftSummary | null;
  colleagues: string[];
  // задържани заради проблем със стоката (повредено, липсва…)
  held: number;
};

export const summarizeDeliveryDrafts = (
  mine: DraftSummary | null,
  others: DraftSummary[],
): DeliveryDraftsSummary => ({
  total: (mine ? 1 : 0) + others.length,
  mine,
  colleagues: [
    ...new Set(others.map((draft) => draft.ownerName?.trim()).filter((name): name is string => !!name)),
  ],
  held: [mine, ...others].filter((draft) => draft?.status === "held").length,
});

// ─── 3 Плащане ─────────────────────────────────────────────────────────────
// `overdue` — неплатените над OVERDUE_PAYMENT_DAYS (14) дни: критичен сигнал
export type PaymentsSummary = {
  count: number;
  total: number;
  oldestDays: number | null;
  overdue: number;
};

export const summarizePayable = (payable: PayableDelivery[], now: Dayjs = dayjs()): PaymentsSummary => {
  const ages = payable.map((doc) => payableAgeDays(doc, now));
  return {
    count: payable.length,
    total: roundMoney(payable.reduce((sum, doc) => sum + (doc.totalValueWithVat || 0), 0)),
    oldestDays: ages.length ? Math.max(...ages) : null,
    overdue: ages.filter((age) => age > OVERDUE_PAYMENT_DAYS).length,
  };
};

// ─── 4 Изписване · 6 Прехвърляне (днес) ────────────────────────────────────
export type TodaySummary = { count: number; total: number | null };

// Изписаните днес в текущия склад — като началния екран на „Изписване"
export const summarizeWriteoffsToday = (writeoffs: Writeoff[]): TodaySummary => {
  const sums = sumActiveWriteoffs(writeoffs);
  return { count: sums.count, total: roundMoney(sums.gross) };
};

// Прехвърлените днес от всички складове — по кога са въведени, без анулираните
export const summarizeTransfersToday = (
  transfers: TransferDocument[],
  now: Dayjs = dayjs(),
): TodaySummary => ({
  count: transfers.filter(
    (transfer) => !isTransferAnnulled(transfer) && dayjs(enteredAtOf(transfer)).isSame(now, "day"),
  ).length,
  total: null,
});

// ─── 5 Инвентаризация ──────────────────────────────────────────────────────
export type StocktakeSummary = {
  warehouses: number;
  // Най-отдавна броеният склад; ageDays null — никога не е броен
  stalest: { warehouseId: number; warehouseName: string; ageDays: number | null } | null;
  // Незавършените броения на ресторанта — моята първа, после на колегите.
  // Критичен сигнал при ВСИЧКИ потребители: броенето държи наличностите неверни.
  unfinished: DraftSummary[];
};

export const summarizeStocktakes = (
  warehouses: Warehouse[],
  latest: LatestStocktake[],
  now: Dayjs = dayjs(),
  drafts: { mine: DraftSummary | null; others: DraftSummary[] } = { mine: null, others: [] },
): StocktakeSummary => {
  const latestByWarehouse = new Map(latest.map((item) => [item.warehouseId, item]));
  let stalest: StocktakeSummary["stalest"] = null;
  for (const warehouse of warehouses) {
    const item = latestByWarehouse.get(warehouse.id);
    const ageDays = item ? calendarDaysSince(item.date, now) : null;
    const rank = ageDays ?? Number.POSITIVE_INFINITY;
    const stalestRank = stalest ? (stalest.ageDays ?? Number.POSITIVE_INFINITY) : -1;
    if (rank > stalestRank) {
      stalest = { warehouseId: warehouse.id, warehouseName: warehouse.name, ageDays };
    }
  }
  const unfinished = [...(drafts.mine ? [drafts.mine] : []), ...drafts.others];
  return { warehouses: warehouses.length, stalest, unfinished };
};

// ─── 7 Излишък от продажби ─────────────────────────────────────────────────
export type ShortageSummary = { products: number; warehouses: number; gross: number };

// Продукт в недостиг в два склада е ЕДИН продукт тук (редовете са по склад)
export const summarizeShortages = (rows: ShortageRow[]): ShortageSummary => ({
  products: new Set(rows.map((row) => row.productId)).size,
  warehouses: new Set(rows.map((row) => row.warehouseId)).size,
  gross: roundMoney(rows.reduce((sum, row) => sum + row.quantityGross, 0)),
});

// ─── 8 Взаимозаменяеми ─────────────────────────────────────────────────────
export type InterchangeSummary = { rules: number };

export const summarizeInterchange = (groups: InterchangeGroup[]): InterchangeSummary => ({
  rules: groups.length,
});

// ─── Кутиите ───────────────────────────────────────────────────────────────
export type DashboardSummaries = {
  posting: Loadable<PostingSummary>;
  delivery: Loadable<DeliveryDraftsSummary>;
  "delivery-payments": Loadable<PaymentsSummary>;
  writeoff: Loadable<TodaySummary>;
  stocktake: Loadable<StocktakeSummary>;
  transfer: Loadable<TodaySummary>;
  shortage: Loadable<ShortageSummary>;
  interchangeables: Loadable<InterchangeSummary>;
};

export type DashboardBoxSlug = keyof DashboardSummaries;

// Кутиите в реда на менюто (клавиш 1 = първата операция)
export const DASHBOARD_BOX_SLUGS = OPERATIONS_NAV_GROUP.items.map(
  (item) => item.slug,
) as DashboardBoxSlug[];

// Сигналът на кутията (docs/inventory.md §Плътност и сигнали):
//  • critical (червена рамка + черта, лента най-горе) — незавършена работа, която
//    държи наличностите или парите неверни и чака човек: незавършена
//    инвентаризация (винаги), отчет, неразнесен над 2 дни, задържана доставка,
//    неплатена доставка над 14 дни;
//  • attention (тънък кехлибарен акцент) — неплатени (по-млади), излишък от продажби.
// Чакащ, но незакъснял отчет и днешните чернови са нормален ход на деня — без
// сигнал. Незаредена кутия — също.
export type DashboardSignal = "critical" | "attention";

export const attentionLevelOf = (
  slug: DashboardBoxSlug,
  summaries: DashboardSummaries,
): DashboardSignal | null => {
  const box = summaries[slug];
  if (box.state !== "ready") return null;
  switch (slug) {
    case "posting":
      return (box.value as PostingSummary).overdue > 0 ? "critical" : null;
    case "delivery":
      return (box.value as DeliveryDraftsSummary).held > 0 ? "critical" : null;
    case "delivery-payments": {
      const payments = box.value as PaymentsSummary;
      return payments.overdue > 0 ? "critical" : payments.count > 0 ? "attention" : null;
    }
    case "stocktake":
      return (box.value as StocktakeSummary).unfinished.length > 0 ? "critical" : null;
    case "shortage":
      return (box.value as ShortageSummary).products > 0 ? "attention" : null;
    default:
      return null;
  }
};

// Кутиите с критичен сигнал в реда на тежестта за лентата най-горе:
// незавършената инвентаризация е първа (винаги), после закъснелите отчети,
// задържаните и просрочените плащания
const CRITICAL_ORDER: DashboardBoxSlug[] = ["stocktake", "posting", "delivery", "delivery-payments"];

export const criticalBoxSlugs = (summaries: DashboardSummaries): DashboardBoxSlug[] =>
  CRITICAL_ORDER.filter((slug) => attentionLevelOf(slug, summaries) === "critical");

// ─── Клавиатурата на решетката ─────────────────────────────────────────────
// Колоните следват брейкпойнтите на `useViewport`: 1 на телефон, 2 на
// таблет, 3 от 1024 px — стрелките ↑/↓ скачат през толкова кутии.
export const dashboardColumnsFor = (viewport: { isNarrow: boolean; isDesktop: boolean }) =>
  viewport.isNarrow ? 1 : viewport.isDesktop ? 3 : 2;

// Съседната кутия по стрелка/Home/End; null — няма накъде (без превъртане)
export const neighborBoxIndex = (
  index: number,
  key: string,
  columns: number,
  total: number,
): number | null => {
  switch (key) {
    case "ArrowRight":
      return index + 1 < total ? index + 1 : null;
    case "ArrowLeft":
      return index > 0 ? index - 1 : null;
    case "ArrowDown": {
      if (index + columns < total) return index + columns;
      // Последният ред е непълен: ↓ от колона без кутия отдолу → последната
      const lastRowStart = Math.floor((total - 1) / columns) * columns;
      return index < lastRowStart ? total - 1 : null;
    }
    case "ArrowUp":
      return index - columns >= 0 ? index - columns : null;
    case "Home":
      return 0;
    case "End":
      return total - 1;
    default:
      return null;
  }
};

// Клавиш 1–9 (горният ред или цифровият блок) → индекс на кутия; с Ctrl/Cmd/
// Alt/Shift — не е наш (Shift+1 е „!"). `code` покрива подредби, в които
// горният ред не дава цифри без Shift.
export const boxIndexOfKey = (
  event: Pick<KeyboardEvent, "key" | "code" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">,
  total: number,
): number | null => {
  if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return null;
  const digit = /^[1-9]$/.test(event.key)
    ? Number(event.key)
    : /^Digit[1-9]$/.test(event.code)
      ? Number(event.code.slice(5))
      : null;
  if (digit == null || digit > total) return null;
  return digit - 1;
};
